import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Lock, ShieldAlert, TerminalSquare } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { ApiError } from '../lib/api.js';
import { useToast } from '../components/Toast.js';
import { ThemeToggle } from '../components/layout/AppShell.js';
import { PRODUCT_NAME, Wordmark } from '../components/Brand.js';
import { Alert, Avatar, Badge, Button, Card, Field, Input, LoadingPane } from '../components/ui.js';

/**
 * The only unauthenticated route.
 *
 * Microsoft Entra is the way in. The local panel below it exists so the
 * platform can be run without an identity provider attached, and it is
 * labelled for exactly what it is rather than dressed up as an alternative
 * front door — when Entra is configured, this page must not leave any doubt
 * about which button is the real one.
 */
export function SignIn() {
  const { config, devLogin } = useAuth();
  const { toastError } = useToast();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const redirectTo = params.get('redirectTo');
  const error = params.get('error');

  const providers = config?.providers ?? [];
  const hasEntra = providers.includes('entra');
  const hasDev = providers.includes('dev');
  const domain = config?.allowedDomain ?? 'the AuxoAI directory';
  const tenant = config?.tenantName ?? 'AuxoAI';

  const loginHref = redirectTo
    ? `/api/auth/login?redirectTo=${encodeURIComponent(redirectTo)}`
    : '/api/auth/login';

  const signInAs = async (address: string): Promise<void> => {
    setPendingEmail(address);
    try {
      await devLogin(address);
    } catch (err) {
      toastError(
        err,
        err instanceof ApiError ? err.message : 'Local sign-in is not available here.',
      );
    } finally {
      setPendingEmail(null);
    }
  };

  return (
    <div className="min-h-full bg-canvas flex flex-col">
      <header className="border-b border-chrome-line bg-chrome text-chrome-ink">
        <div className="mx-auto max-w-[80rem] px-4 sm:px-6 h-14 flex items-center gap-3">
          <Wordmark onChrome />
          <span className="flex-1" />
          <ThemeToggle />
        </div>
      </header>

      <main id="main" className="flex-1 px-4 sm:px-6 py-10 lg:py-16">
        <div className="mx-auto grid w-full max-w-[64rem] grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] lg:gap-16">
          <BrandPanel tenant={tenant} />

          <div className="flex min-w-0 flex-col gap-5">
            {error ? <SignInError error={error} domain={domain} tenant={tenant} /> : null}

            <Card className="p-6">
              <p className="eyebrow">Internal · AuxoAI directory only</p>
              <h2 className="text-2xl font-semibold tracking-tight text-ink mt-2">Sign in</h2>
              <p className="text-sm text-ink-muted mt-2 leading-relaxed">
                {PRODUCT_NAME} is {tenant}’s internal learning platform. Access is limited to the {tenant}{' '}
                directory — accounts are created the first time someone with an{' '}
                <span className="font-medium text-ink">@{domain}</span> address signs in, and no
                other address can get in.
              </p>

              {!config ? (
                <LoadingPane label="Checking how this deployment signs you in…" />
              ) : (
                <div className="mt-6 flex flex-col gap-4">
                  {hasEntra ? (
                    <div>
                      {/*
                        A real navigation, not fetch: this is an OIDC redirect and
                        the browser has to follow it.
                      */}
                      <a
                        href={loginHref}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-control border border-brand bg-brand px-5 py-3 text-sm font-semibold text-brand-ink hover:bg-brand-strong hover:border-brand-strong transition-colors duration-150 ease-out"
                      >
                        <Lock size={16} aria-hidden />
                        Continue with Microsoft
                      </a>
                      <p className="text-xs text-ink-subtle mt-2 text-center">
                        You will be sent to Microsoft to sign in, then straight back here.
                      </p>
                    </div>
                  ) : null}

                  {!hasEntra && !hasDev ? (
                    <Alert tone="danger" title="No sign-in method is configured">
                      This deployment has neither Microsoft sign-in nor local sign-in enabled, so
                      there is no way in until an administrator configures one.
                    </Alert>
                  ) : null}

                  {hasDev ? (
                    <DevPanel
                      accounts={config.devAccounts}
                      demoted={hasEntra}
                      email={email}
                      onEmail={setEmail}
                      pendingEmail={pendingEmail}
                      onSignIn={signInAs}
                    />
                  ) : null}
                </div>
              )}
            </Card>

            <p className="text-xs text-ink-subtle text-center leading-relaxed">
              Video, notes, quizzes, guides and the capstone brief are internal material. Nothing
              you do here is graded, and the AI panel inside a session keeps no record of what you
              ask.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * The first thing anyone sees: the name, the line, and three plain sentences
 * about what is inside. No numbers and no claims — the platform is described,
 * not sold. Below the lg breakpoint it sits above the sign-in card.
 */
function BrandPanel({ tenant }: { tenant: string }) {
  const inside = [
    {
      label: 'Courses',
      text: 'Sessions you watch, practise and build, in the order your trainers set.',
    },
    {
      label: 'Guides',
      text: 'How-tos written by trainers and admins, open to everyone in the directory.',
    },
    {
      label: 'Nothing graded',
      text: 'Quizzes are feedback for you. The ticks on a project are yours to set.',
    },
  ];
  return (
    <section aria-labelledby="brand-title" className="min-w-0 lg:pt-6">
      {/* The logo carries the name and the line, so it is the heading. */}
      <h1 id="brand-title" className="max-w-full">
        <Wordmark className="h-20 sm:h-24 max-w-full" />
      </h1>
      <p className="eyebrow mt-4">{tenant}’s learning platform</p>

      <dl className="mt-8 grid max-w-prose gap-4 border-l-2 border-brand pl-5">
        {inside.map((item) => (
          <div key={item.label}>
            <dt className="text-sm font-semibold text-ink">{item.label}</dt>
            <dd className="mt-0.5 text-sm text-ink-muted leading-relaxed">{item.text}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The OIDC callback redirects failures back here with the reason in the query
 * string. A wrong tenant or a personal address is the common one, and it has to
 * read as "you are not authorized, here is who is" rather than as a crash.
 */
function SignInError({
  error,
  domain,
  tenant,
}: {
  error: string;
  domain: string;
  tenant: string;
}) {
  const lower = error.toLowerCase();
  const isDomainRejection =
    lower.includes('domain') ||
    lower.includes('tenant') ||
    lower.includes('not authorized') ||
    lower.includes('unauthorized') ||
    lower.includes('@');
  const isDeactivated = lower.includes('deactivated');

  return (
    <Card className="border-danger/40 bg-danger-wash p-5">
      <div className="flex items-start gap-3">
        <span aria-hidden className="mt-0.5 text-danger shrink-0">
          <ShieldAlert size={20} />
        </span>
        <div className="min-w-0" role="alert">
          <h2 className="font-semibold text-ink">
            {isDeactivated
              ? 'That account is no longer active'
              : isDomainRejection
                ? 'That account is not allowed in'
                : 'Sign-in did not complete'}
          </h2>
          <p className="text-sm text-ink mt-1.5 leading-relaxed">{error}</p>
          {isDomainRejection && !isDeactivated ? (
            <p className="text-sm text-ink-muted mt-2 leading-relaxed">
              This platform accepts only {tenant} work accounts in the{' '}
              <span className="font-medium text-ink">@{domain}</span> directory. A personal
              Microsoft account, a guest account, or an address from another tenant will be turned
              away even if the password is correct. Sign in with your{' '}
              <span className="font-medium text-ink">@{domain}</span> address, or ask IT to add you
              to the directory.
            </p>
          ) : null}
          {isDeactivated ? (
            <p className="text-sm text-ink-muted mt-2 leading-relaxed">
              The account exists but has been switched off. An administrator can re-enable it from
              the console.
            </p>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function DevPanel({
  accounts,
  demoted,
  email,
  onEmail,
  pendingEmail,
  onSignIn,
}: {
  accounts: { email: string; name: string; label: string }[];
  /** True when Entra is configured, in which case this must not compete with it. */
  demoted: boolean;
  email: string;
  onEmail: (value: string) => void;
  pendingEmail: string | null;
  onSignIn: (email: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(!demoted);

  const panel = (
    <div className="rounded-control border border-warn/40 bg-warn-wash p-4">
      <div className="flex items-start gap-2.5">
        <span aria-hidden className="mt-0.5 text-warn shrink-0">
          <TerminalSquare size={18} />
        </span>
        <div className="min-w-0">
          <h2 className="font-semibold text-ink text-sm flex flex-wrap items-center gap-2">
            Development sign-in
            <Badge tone="warn">Bypasses the identity provider</Badge>
          </h2>
          <p className="text-xs text-ink-muted mt-1.5 leading-relaxed">
            This panel signs you in without Microsoft, without a password and without any identity
            check at all. It exists so the platform can be run locally and it is only offered
            because this deployment has it switched on. It is not the way anyone signs in for real.
          </p>
        </div>
      </div>

      {accounts.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-1.5">
          {accounts.map((account) => (
            <li key={account.email}>
              <button
                type="button"
                onClick={() => void onSignIn(account.email)}
                disabled={pendingEmail !== null}
                aria-busy={pendingEmail === account.email}
                className="w-full flex items-center gap-3 text-left rounded-control border border-line bg-surface px-3 py-2 hover:bg-surface-2 disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                <Avatar name={account.name} size={30} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-ink truncate">
                    {account.name}
                  </span>
                  <span className="block text-xs text-ink-subtle truncate">{account.label}</span>
                  <span className="block text-xs text-ink-subtle truncate">{account.email}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <form
        className="mt-4 flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = email.trim();
          if (trimmed) void onSignIn(trimmed);
        }}
      >
        <Field
          label="Or sign in as any directory address"
          htmlFor="dev-email"
          hint="The account is created on the spot if it does not exist. The address still has to be in the allowed domain."
        >
          <Input
            id="dev-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            placeholder="someone@example.com"
          />
        </Field>
        <Button
          type="submit"
          variant="secondary"
          size="sm"
          className="self-start"
          disabled={!email.trim() || pendingEmail !== null}
          loading={pendingEmail === email.trim()}
        >
          Sign in locally
        </Button>
      </form>
    </div>
  );

  if (!demoted) return panel;

  return (
    <div className="border-t border-line pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-xs text-ink-subtle hover:text-ink underline underline-offset-2"
      >
        {open ? 'Hide' : 'Show'} development sign-in (bypasses Microsoft — not for normal use)
      </button>
      {open ? <div className="mt-3">{panel}</div> : null}
    </div>
  );
}
